import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { BoardToolId } from "../../editor/types";
import type { SceneObject } from "../../editor/SceneHost";
import { worldToClient, type BoardViewportState } from "../../lib/board/boardViewport";
import { getHomogeneousType, unionBounds, type Bounds } from "../../editor/selectionBounds";
import { WbBoardScene } from "../../lib/board/wb-board-scene";
import { readObjectStyle } from "../../lib/board/sceneStyle";
import {
  buildFillStylePatch,
  buildStrokeStylePatch,
  supportsStrokeStyle,
} from "../../editor/style/applyStylePatch";
import { StrokeStylePicker } from "./StrokeStylePicker";
import { setStickySettings, STICKY_BG_PRESETS } from "../../editor/stickySession";
import {
  setTextSettings,
  TEXT_SIZE_MAX,
  TEXT_SIZE_MIN,
  TEXT_SIZE_PRESETS,
} from "../../editor/textSession";
import {
  patchTextRunsAndFit,
  patchTextStyleAndFit,
  textContent,
  textFill,
} from "../../editor/textObject";
import { TextFontPicker, TextSizeField } from "./TextFontControls";
import { TextFillPicker, TextLinkButton } from "./TextExtraControls";
import { ensureFontLoaded } from "../../editor/textFonts";
import {
  objectRuns,
  runsPlainText,
  runsReplace,
  runsSetHighlight,
  runsSetHref,
} from "../../editor/textRuns";
import { indentListLine, lineBounds, toggleListLines } from "../../editor/textLists";
import type { TextRun } from "@bezpart-board/shared";

/** Маркер: пастель, под которой чёрный текст читается. */
const HIGHLIGHT_COLORS = ["#fef08a", "#bbf7d0", "#bfdbfe", "#fbcfe8", "#fed7aa"] as const;

/** Текст надписи кусками — даже если оформление у неё общее. */
function runsOfObject(obj: SceneObject): TextRun[] {
  const runs = objectRuns(obj);
  if (runs) return runs;
  const text = textContent(obj);
  return text ? [{ text }] : [];
}

/** Вложить или вынести каждый пункт надписи (снизу вверх — смещения не плывут). */
function indentAllListLines(runs: TextRun[], delta: 1 | -1): TextRun[] {
  let out = runs;
  const text = runsPlainText(runs);
  const starts: number[] = [0];
  for (let i = 0; i < text.length; i++) if (text[i] === "\n") starts.push(i + 1);
  for (const start of starts.reverse()) {
    const current = runsPlainText(out);
    const { end } = lineBounds(current, start);
    const edit = indentListLine(current, Math.min(end, start + 1), delta);
    if (edit) out = runsReplace(out, edit.start, edit.end, edit.text);
  }
  return out;
}
import { applyMarkToTextSelection } from "../../editor/textFormatBridge";
import { FRAME_TINT_PRESETS, frameTintColor } from "../../editor/frame/frameContext";
import {
  setShapeSettings,
  SHAPE_FILL_PRESETS,
  SHAPE_STROKE_PRESETS,
  SHAPE_WIDTH_PRESETS,
} from "../../editor/shapeSession";
import { setLineSettings } from "../../editor/lineSession";
import { shapeSupportsCenter } from "../../editor/geometry/shapeCenter";

const TEXT_COLORS = [
  "#1a1a2e",
  "#b91c1c",
  "#166534",
  "#1d4ed8",
  "#6b21a8",
  "#9a3412",
  "#334155",
];

const SHAPE_TYPES = new Set(["rect", "ellipse", "triangle", "diamond", "star", "geom"]);



type Props = {
  canEdit: boolean;
  activeTool: BoardToolId;
  selectedIds: string[];
  suppressDuringDrag?: boolean;
  /** Объект, в котором сейчас набирают текст: панель нужна и тогда. */
  editingObjectId?: string | null;
  sceneRef: React.RefObject<InstanceType<typeof WbBoardScene> | null>;
  viewportRef: React.RefObject<HTMLDivElement | null>;
  viewport: BoardViewportState;
  getLiveViewport: () => BoardViewportState;
  layoutTick: number;
  onPresentPdf?: (objectId: string) => void;
  onBroadcastPdf?: (objectId: string) => void;
  onDownloadPresentation?: (objectId: string) => void | Promise<void>;
  onExportFramePdf?: (objectId: string) => void | Promise<void>;
  onOpenComments?: (objectId: string) => void;
  exportBusy?: boolean;
};



const TOOLBAR_GAP_PX = 12;

/**
 * Выделенные объекты из сцены. Через Set, а не ids.includes: панель
 * пересчитывается при каждом кадре перетаскивания, и линейный поиск внутри
 * filter давал квадрат — на выделении из сотен объектов вкладка подвисала,
 * а панель над ними «залипала».
 */
function pickSelected(objects: SceneObject[], selectedIds: string[]): SceneObject[] {
  const set = new Set(selectedIds);
  return objects.filter((o) => set.has(o.id));
}

function boundsIntersect(a: Bounds, b: Bounds): boolean {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}

function visibleWorldBounds(
  viewportEl: HTMLElement,
  cam: BoardViewportState
): Bounds {
  const rect = viewportEl.getBoundingClientRect();
  const scale = cam.scale > 0 ? cam.scale : 1;
  return {
    x: -cam.panX / scale,
    y: -cam.panY / scale,
    w: rect.width / scale,
    h: rect.height / scale,
  };
}

export function BoardSelectionToolbar({
  canEdit,
  activeTool,
  selectedIds: selectedIdsProp,
  suppressDuringDrag = false,
  editingObjectId = null,
  sceneRef,
  viewportRef,
  viewport,
  getLiveViewport,
  layoutTick,
  onPresentPdf,
  onBroadcastPdf,
  onDownloadPresentation,
  onExportFramePdf,
  onOpenComments,
  exportBusy = false,
}: Props) {
  const rootRef = useRef<HTMLDivElement>(null);
  // Пока идёт набор, панель работает с этим объектом, даже если выделение снято.
  const selectedIds = editingObjectId ? [editingObjectId] : selectedIdsProp;
  const [isViewportNavigating, setIsViewportNavigating] = useState(false);

  // Во время набора текста панель показываем при любом инструменте:
  // иначе, поставив текст инструментом «Текст», нельзя было сразу
  // поменять размер или цвет — приходилось выходить и выделять заново.
  const toolbarHidden =
    suppressDuringDrag ||
    isViewportNavigating ||
    (activeTool !== "select" && !editingObjectId);

  useEffect(() => {
    function onViewportNavigating(ev: Event) {
      setIsViewportNavigating(Boolean((ev as CustomEvent<boolean>).detail));
    }
    window.addEventListener("wb:viewport-navigating", onViewportNavigating);
    return () => window.removeEventListener("wb:viewport-navigating", onViewportNavigating);
  }, []);

  const pinToSelection = useCallback(() => {
    const el = rootRef.current;
    const vpEl = viewportRef.current;
    const scene = sceneRef.current;
    if (!el || !vpEl || !scene || selectedIds.length < 1) return;
    if (vpEl.classList.contains("is-navigating")) return;
    const objects = pickSelected(scene.getObjects(), selectedIds);
    const bounds = unionBounds(objects);
    if (!bounds) return;
    const cam = getLiveViewport() ?? viewport;
    const view = visibleWorldBounds(vpEl, cam);
    el.classList.toggle("is-offscreen", !boundsIntersect(bounds, view));
    const tw = el.offsetWidth;
    const th = el.offsetHeight;
    if (tw < 2 || th < 2) return;
    const rect = vpEl.getBoundingClientRect();
    const anchorX = bounds.x + bounds.w / 2;
    const anchorY = bounds.y;
    const client = worldToClient(anchorX, anchorY, rect, cam);
    el.style.left = `${client.x}px`;
    el.style.top = `${client.y}px`;
    el.style.maxWidth = `${Math.max(120, vpEl.clientWidth - 16)}px`;
    el.style.transform = `translate(-50%, calc(-100% - ${TOOLBAR_GAP_PX}px))`;
  }, [getLiveViewport, sceneRef, selectedIds, viewport, viewportRef]);

  useLayoutEffect(() => {
    if (toolbarHidden || !canEdit || selectedIds.length < 1) return;
    pinToSelection();
    const onLive = () => pinToSelection();
    window.addEventListener("wb:live-viewport", onLive);
    window.addEventListener("resize", onLive);
    const el = rootRef.current;
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(onLive) : null;
    if (el && ro) ro.observe(el);
    return () => {
      window.removeEventListener("wb:live-viewport", onLive);
      window.removeEventListener("resize", onLive);
      ro?.disconnect();
    };
  }, [pinToSelection, layoutTick, toolbarHidden, canEdit, selectedIds.length]);

  useEffect(() => {
    if (toolbarHidden) return;
    let raf = 0;
    let dragging = false;
    const tick = () => {
      if (!dragging) return;
      pinToSelection();
      raf = requestAnimationFrame(tick);
    };
    const onSelectionDrag = (ev: Event) => {
      dragging = Boolean((ev as CustomEvent<boolean>).detail);
      cancelAnimationFrame(raf);
      if (dragging) {
        pinToSelection();
        raf = requestAnimationFrame(tick);
      } else {
        pinToSelection();
      }
    };
    window.addEventListener("wb:selection-drag", onSelectionDrag);
    return () => {
      window.removeEventListener("wb:selection-drag", onSelectionDrag);
      cancelAnimationFrame(raf);
    };
  }, [pinToSelection, toolbarHidden]);

  useEffect(() => {
    if (!canEdit || selectedIds.length < 1 || toolbarHidden) return;
    function onKey(ev: KeyboardEvent) {
      if (ev.key !== "Escape") return;
      const t = ev.target as HTMLElement | null;
      if (t && (t.isContentEditable || t.tagName === "INPUT" || t.tagName === "TEXTAREA")) return;
      ev.preventDefault();
      sceneRef.current?.setSelection([]);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [canEdit, selectedIds.length, toolbarHidden, sceneRef]);

  const clearSelection = useCallback(() => {
    sceneRef.current?.setSelection([]);
  }, [sceneRef]);



  const applyPatch = useCallback(
    (patch: Record<string, unknown>) => {
      const scene = sceneRef.current;
      if (!scene || !selectedIds.length) return;
      const patches = selectedIds
        .map((id) => ({ id, patch }))
        .filter((p) => {
          const obj = scene.findObject(p.id);
          return obj && !obj.locked;
        });
      if (!patches.length) return;
      void scene.commitBatchPatches(patches, `style-${Date.now()}`);
    },
    [sceneRef, selectedIds]
  );

  const applyTextStyle = useCallback(
    (style: Record<string, unknown>) => {
      const scene = sceneRef.current;
      if (!scene || !selectedIds.length) return;
      const patches = selectedIds.flatMap((id) => {
        const obj = scene.findObject(id);
        if (!obj || obj.locked) return [];
        return [{ id, patch: patchTextStyleAndFit(obj, style) }];
      });
      if (!patches.length) return;
      void scene.commitBatchPatches(patches, `style-${Date.now()}`);
    },
    [sceneRef, selectedIds]
  );



  /** Переписать текст выделенных надписей (список, маркер на всю надпись). */
  const applyTextRuns = useCallback(
    (transform: (runs: TextRun[]) => TextRun[]) => {
      const scene = sceneRef.current;
      if (!scene || !selectedIds.length) return;
      const patches = selectedIds.flatMap((id) => {
        const obj = scene.findObject(id);
        if (!obj || obj.locked || obj.type !== "text") return [];
        const before = runsOfObject(obj);
        const after = transform(before);
        if (JSON.stringify(after) === JSON.stringify(before)) return [];
        return [{ id, patch: patchTextRunsAndFit(obj, after) }];
      });
      if (!patches.length) return;
      void scene.commitBatchPatches(patches, `text-runs-${Date.now()}`);
    },
    [sceneRef, selectedIds]
  );

  const selectedObjects = sceneRef.current
    ? pickSelected(sceneRef.current.getObjects(), selectedIds)
    : [];
  const homogeneousType = getHomogeneousType(selectedObjects);
  const allLocked =
    selectedObjects.length > 0 && selectedObjects.every((o) => o.locked);
  const anyLocked = selectedObjects.some((o) => o.locked);
  const strokeStyle =
    selectedObjects[0] && supportsStrokeStyle(homogeneousType)
      ? readObjectStyle(selectedObjects[0])
      : null;
  // Точку в центре предлагаем только плоским фигурам: у объёмных её негде поставить.
  const centerShapes = selectedObjects.filter((o) =>
    shapeSupportsCenter(o as { type: string; kind?: unknown })
  );
  const centerOn =
    centerShapes.length > 0 &&
    centerShapes.every((o) => (o as { showCenter?: boolean }).showCenter === true);

  if (!canEdit || selectedIds.length < 1 || toolbarHidden) {
    return null;
  }

  const compactToolbar =
    selectedIds.length === 1 &&
    (homogeneousType === "text" || homogeneousType === "sticky");

  const toolbar = (
    <div
      ref={rootRef}
      className={`wb-selection-toolbar${compactToolbar ? " wb-selection-toolbar--compact" : ""}`}
      role="toolbar"
      aria-label="Свойства объекта"
      onPointerDownCapture={(ev) => ev.stopPropagation()}
      onPointerDown={(ev) => ev.stopPropagation()}
      onMouseDown={(ev) => {
        ev.stopPropagation();
        // Во время набора клик по панели не должен уводить курсор из текста:
        // иначе набор закрывался, а правка размера/цвета применялась уже «мимо».
        const target = ev.target as HTMLElement | null;
        if (editingObjectId && !target?.closest("input, select, textarea")) {
          ev.preventDefault();
        }
      }}
    >
      <div className="wb-selection-toolbar__section wb-selection-toolbar__actions">
        <button
          type="button"
          className="wb-selection-toolbar__action-btn wb-selection-toolbar__action-btn--icon"
          title="На задний план"
          aria-label="На задний план"
          onMouseDown={(ev) => ev.stopPropagation()}
          onClick={(ev) => {
            ev.preventDefault();
            ev.stopPropagation();
            void sceneRef.current?.adjustZOrder("back");
          }}
        >
          ↓
        </button>
        <button
          type="button"
          className="wb-selection-toolbar__action-btn wb-selection-toolbar__action-btn--icon"
          title="На передний план"
          aria-label="На передний план"
          onMouseDown={(ev) => ev.stopPropagation()}
          onClick={(ev) => {
            ev.preventDefault();
            ev.stopPropagation();
            void sceneRef.current?.adjustZOrder("front");
          }}
        >
          ↑
        </button>
        <button
          type="button"
          className={`wb-selection-toolbar__action-btn${allLocked ? " is-active" : ""}`}
          title={allLocked ? "Разблокировать" : "Заблокировать"}
          aria-label={allLocked ? "Разблокировать" : "Заблокировать"}
          onClick={() => void sceneRef.current?.toggleSelectionLock()}
        >
          {allLocked ? "откр" : "лок"}
        </button>
        {selectedIds.length === 1 && onOpenComments ? (
          <button
            type="button"
            className="wb-selection-toolbar__action-btn"
            title="Комментарии"
            aria-label="Комментарии к объекту"
            onMouseDown={(ev) => ev.stopPropagation()}
            onClick={(ev) => {
              ev.preventDefault();
              ev.stopPropagation();
              const id = selectedIds[0];
              if (id) onOpenComments(id);
            }}
          >
            ком
          </button>
        ) : null}
        {canEdit ? (
          // С планшета и телефона нет Ctrl+C и Ctrl+V: «копия» кладёт дубль
          // рядом, «копир» — в буфер, чтобы вставить на другой доске.
          <button
            type="button"
            className="wb-selection-toolbar__action-btn"
            title="Сделать копию рядом (Ctrl+D)"
            aria-label="Сделать копию выделенного"
            onMouseDown={(ev) => ev.stopPropagation()}
            onClick={(ev) => {
              ev.preventDefault();
              ev.stopPropagation();
              void sceneRef.current?.duplicateSelection(16);
            }}
          >
            копия
          </button>
        ) : null}
        {canEdit ? (
          <button
            type="button"
            className="wb-selection-toolbar__action-btn"
            title="Копировать в буфер (Ctrl+C) — вставить можно и на другой доске"
            aria-label="Копировать выделенное в буфер"
            onMouseDown={(ev) => ev.stopPropagation()}
            onClick={(ev) => {
              ev.preventDefault();
              ev.stopPropagation();
              sceneRef.current?.copySelection();
            }}
          >
            копир
          </button>
        ) : null}
        {canEdit ? (
          // Без неё объект удалялся только клавишей Delete: с планшета — никак,
          // а у виджетов с редактором Delete уходил в текст.
          <button
            type="button"
            className="wb-selection-toolbar__action-btn wb-selection-toolbar__action-btn--danger"
            title="Удалить (Delete)"
            aria-label="Удалить выделенное"
            onMouseDown={(ev) => ev.stopPropagation()}
            onClick={(ev) => {
              ev.preventDefault();
              ev.stopPropagation();
              void sceneRef.current?.deleteSelected();
            }}
          >
            удал
          </button>
        ) : null}
        <button
          type="button"
          className="wb-selection-toolbar__action-btn wb-selection-toolbar__action-btn--close"
          title="Снять выделение (Esc)"
          aria-label="Снять выделение"
          onMouseDown={(ev) => ev.stopPropagation()}
          onClick={(ev) => {
            ev.preventDefault();
            ev.stopPropagation();
            clearSelection();
          }}
        >
          ×
        </button>
      </div>

      {selectedIds.length > 1 ? (
        <div className="wb-selection-toolbar__section wb-selection-toolbar__align">
          <span className="wb-selection-toolbar__label">Выровнять</span>
          <div className="wb-selection-toolbar__actions">
            {(
              [
                ["left", "По левому краю", "←│"],
                ["center", "По центру горизонтально", "││"],
                ["right", "По правому краю", "│→"],
                ["top", "По верхнему краю", "┬"],
                ["middle", "По центру вертикально", "─"],
                ["bottom", "По нижнему краю", "┴"],
              ] as const
            ).map(([mode, title, icon]) => (
              <button
                key={mode}
                type="button"
                className="wb-selection-toolbar__action-btn"
                title={title}
                aria-label={title}
                disabled={allLocked}
                onClick={() => void sceneRef.current?.alignSelection(mode)}
              >
                {icon}
              </button>
            ))}
          </div>
          {selectedIds.length >= 3 ? (
            <>
              <span className="wb-selection-toolbar__label">Распределить</span>
              <div className="wb-selection-toolbar__actions">
                <button
                  type="button"
                  className="wb-selection-toolbar__action-btn"
                  title="Равные промежутки по горизонтали"
                  aria-label="Равные промежутки по горизонтали"
                  disabled={allLocked}
                  onClick={() => void sceneRef.current?.distributeSelection("horizontal")}
                >
                  ↔
                </button>
                <button
                  type="button"
                  className="wb-selection-toolbar__action-btn"
                  title="Равные промежутки по вертикали"
                  aria-label="Равные промежутки по вертикали"
                  disabled={allLocked}
                  onClick={() => void sceneRef.current?.distributeSelection("vertical")}
                >
                  ↕
                </button>
              </div>
            </>
          ) : null}
        </div>
      ) : null}



      {homogeneousType === "sticky" ? (
        <div className="wb-selection-toolbar__section">
          <span className="wb-selection-toolbar__label">Цвет</span>
          <div className="wb-selection-toolbar__swatches">
            {STICKY_BG_PRESETS.map((c) => (
              <button
                key={c}
                type="button"
                className="wb-selection-toolbar__swatch"
                style={{
                  background: c,
                  boxShadow: c === "#ffffff" ? "inset 0 0 0 1px #cbd5e1" : undefined,
                }}
                title={c}
                disabled={anyLocked}
                onClick={() => {
                  applyPatch({ color: c });
                  setStickySettings({ backgroundColor: c });
                }}
              />
            ))}
          </div>
        </div>
      ) : null}

      {homogeneousType === "text" || homogeneousType === "sticky" ? (
        <div className="wb-selection-toolbar__section">
          {homogeneousType === "text" ? (
            <>
              <div className="wb-selection-toolbar__swatches" title="Цвет текста">
                {TEXT_COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    className="wb-selection-toolbar__swatch"
                    style={{ background: c }}
                    title={c}
                    aria-label={`Цвет ${c}`}
                    disabled={anyLocked}
                    onClick={() => {
                      // Выделен кусок текста — красим только его.
                      if (applyMarkToTextSelection({ command: "color", value: c })) return;
                      applyTextStyle({ color: c });
                      setTextSettings({ color: c });
                    }}
                  />
                ))}
              </div>
              {(() => {
                const current = selectedObjects[0]
                  ? readObjectStyle(selectedObjects[0]).fontSize
                  : undefined;
                const shown =
                  typeof current === "number" && Number.isFinite(current)
                    ? Math.round(current * 10) / 10
                    : TEXT_SIZE_PRESETS[3];
                const applySize = (next: number) => {
                  const size = Math.min(TEXT_SIZE_MAX, Math.max(TEXT_SIZE_MIN, next));
                  if (applyMarkToTextSelection({ command: "fontSize", value: size })) return;
                  applyTextStyle({ fontSize: size });
                  setTextSettings({ fontSize: size });
                };
                /** Шаг по списку: ближайший пресет в нужную сторону. */
                const step = (dir: -1 | 1) => {
                  const next =
                    dir === 1
                      ? TEXT_SIZE_PRESETS.find((s) => s > shown)
                      : [...TEXT_SIZE_PRESETS].reverse().find((s) => s < shown);
                  applySize(next ?? shown + dir);
                };

                return (
                  <>
                  <TextFontPicker
                    fontFamily={
                      selectedObjects[0]
                        ? readObjectStyle(selectedObjects[0]).fontFamily
                        : undefined
                    }
                    disabled={anyLocked}
                    onPick={(font) => {
                      // Сначала файлы шрифта, потом рамка: иначе она измерилась
                      // бы по запасному шрифту и не совпала с надписью.
                      void ensureFontLoaded(font).then(() => {
                        applyTextStyle({ fontFamily: font.css });
                        setTextSettings({ fontFamily: font.css });
                      });
                    }}
                  />
                  <div className="wb-selection-toolbar__font-size" title="Размер шрифта">
                    <button
                      type="button"
                      className="wb-selection-toolbar__action-btn"
                      title="Меньше"
                      aria-label="Уменьшить размер шрифта"
                      disabled={anyLocked || shown <= TEXT_SIZE_MIN}
                      onClick={() => step(-1)}
                    >
                      −
                    </button>
                    <TextSizeField
                      value={shown}
                      presets={TEXT_SIZE_PRESETS}
                      min={TEXT_SIZE_MIN}
                      max={TEXT_SIZE_MAX}
                      disabled={anyLocked}
                      onApply={applySize}
                    />
                    <button
                      type="button"
                      className="wb-selection-toolbar__action-btn"
                      title="Больше"
                      aria-label="Увеличить размер шрифта"
                      disabled={anyLocked || shown >= TEXT_SIZE_MAX}
                      onClick={() => step(1)}
                    >
                      +
                    </button>
                  </div>
                  </>
                );
              })()}
            </>
          ) : null}
          <div className="wb-selection-toolbar__actions" title="Начертание">
            {(() => {
              const st = selectedObjects[0]
                ? readObjectStyle(selectedObjects[0])
                : null;
              const bold = st?.fontWeight === "bold";
              const italic = st?.fontStyle === "italic";
              const deco = typeof st?.textDecoration === "string" ? st.textDecoration : "";
              const underline = /\bunderline\b/.test(deco);
              const strike = /\bline-through\b/.test(deco);
              const nextDecoration = (u: boolean, s: boolean) => {
                const parts: string[] = [];
                if (u) parts.push("underline");
                if (s) parts.push("line-through");
                return parts.length ? parts.join(" ") : "none";
              };
              return (
                <>
                  <button
                    type="button"
                    className={`wb-selection-toolbar__action-btn${bold ? " is-active" : ""}`}
                    title="Жирный"
                    aria-label="Жирный"
                    aria-pressed={bold}
                    disabled={anyLocked}
                    onClick={() => {
                      if (applyMarkToTextSelection({ command: "bold" })) return;
                      const next = bold ? "normal" : "bold";
                      applyTextStyle({ fontWeight: next });
                      if (homogeneousType === "text") setTextSettings({ fontWeight: next });
                    }}
                  >
                    <strong>B</strong>
                  </button>
                  <button
                    type="button"
                    className={`wb-selection-toolbar__action-btn${italic ? " is-active" : ""}`}
                    title="Курсив"
                    aria-label="Курсив"
                    aria-pressed={italic}
                    disabled={anyLocked}
                    onClick={() => {
                      if (applyMarkToTextSelection({ command: "italic" })) return;
                      const next = italic ? "normal" : "italic";
                      applyTextStyle({ fontStyle: next });
                      if (homogeneousType === "text") setTextSettings({ fontStyle: next });
                    }}
                  >
                    <em>I</em>
                  </button>
                  <button
                    type="button"
                    className={`wb-selection-toolbar__action-btn${underline ? " is-active" : ""}`}
                    title="Подчёркивание"
                    aria-label="Подчёркивание"
                    aria-pressed={underline}
                    disabled={anyLocked}
                    onClick={() => {
                      if (applyMarkToTextSelection({ command: "underline" })) return;
                      const next = nextDecoration(!underline, strike);
                      applyTextStyle({ textDecoration: next });
                      if (homogeneousType === "text") {
                        setTextSettings({ textDecoration: next });
                      }
                    }}
                  >
                    <span style={{ textDecoration: "underline" }}>U</span>
                  </button>
                  <button
                    type="button"
                    className={`wb-selection-toolbar__action-btn${strike ? " is-active" : ""}`}
                    title="Зачёркивание"
                    aria-label="Зачёркивание"
                    aria-pressed={strike}
                    disabled={anyLocked}
                    onClick={() => {
                      if (applyMarkToTextSelection({ command: "strike" })) return;
                      const next = nextDecoration(underline, !strike);
                      applyTextStyle({ textDecoration: next });
                      if (homogeneousType === "text") {
                        setTextSettings({ textDecoration: next });
                      }
                    }}
                  >
                    <span style={{ textDecoration: "line-through" }}>S</span>
                  </button>
                </>
              );
            })()}
          </div>
        </div>
      ) : null}

      {homogeneousType === "text" ? (
        <div className="wb-selection-toolbar__section">
          <div className="wb-selection-toolbar__actions">
            <TextFillPicker
              fill={selectedObjects[0] ? textFill(selectedObjects[0]) : undefined}
              disabled={anyLocked}
              onPick={(c) => applyPatch({ style: { fill: c ?? "none" } })}
            />
            <TextLinkButton
              disabled={anyLocked}
              wholeHref={(() => {
                // Вся надпись — одна ссылка: покажем её адрес в окошке.
                const runs = selectedObjects[0] ? runsOfObject(selectedObjects[0]) : [];
                const first = runs[0]?.href;
                return first && runs.every((r) => r.href === first) ? first : null;
              })()}
              onApplySelection={(href) => applyMarkToTextSelection({ command: "link", value: href })}
              onApplyWhole={(href) =>
                applyTextRuns((runs) => runsSetHref(runs, 0, runsPlainText(runs).length, href))
              }
            />
          </div>
          <div className="wb-selection-toolbar__actions" title="Список">
            {(
              [
                ["bullet", "Маркированный список (или «- » в начале строки)", "•≡"],
                ["number", "Нумерованный список (или «1. » в начале строки)", "1≡"],
              ] as const
            ).map(([kind, title, icon]) => (
              <button
                key={kind}
                type="button"
                className="wb-selection-toolbar__action-btn"
                title={title}
                aria-label={title}
                disabled={anyLocked}
                onClick={() => {
                  if (applyMarkToTextSelection({ command: "list", value: kind })) return;
                  applyTextRuns((runs) => {
                    const text = runsPlainText(runs);
                    const edit = toggleListLines(text, 0, text.length, kind);
                    return runsReplace(runs, edit.start, edit.end, edit.text);
                  });
                }}
              >
                {icon}
              </button>
            ))}
            {(
              [
                [-1, "Вынести пункт уровнем выше (Shift+Tab)", "⇤"],
                [1, "Вложить пункт (Tab)", "⇥"],
              ] as const
            ).map(([delta, title, icon]) => (
              <button
                key={delta}
                type="button"
                className="wb-selection-toolbar__action-btn"
                title={title}
                aria-label={title}
                disabled={anyLocked}
                onClick={() => {
                  if (applyMarkToTextSelection({ command: "indent", value: delta })) return;
                  applyTextRuns((runs) => indentAllListLines(runs, delta));
                }}
              >
                {icon}
              </button>
            ))}
          </div>
          <div className="wb-selection-toolbar__swatches" title="Маркер">
            {HIGHLIGHT_COLORS.map((c) => (
              <button
                key={c}
                type="button"
                className="wb-selection-toolbar__swatch wb-selection-toolbar__swatch--highlight"
                style={{ background: c }}
                title="Маркер"
                aria-label={`Маркер ${c}`}
                disabled={anyLocked}
                onClick={() => {
                  // Выделен кусок — подсвечиваем его, иначе всю надпись.
                  if (applyMarkToTextSelection({ command: "highlight", value: c })) return;
                  applyTextRuns((runs) =>
                    runsSetHighlight(runs, 0, runsPlainText(runs).length, c)
                  );
                }}
              />
            ))}
            <button
              type="button"
              className="wb-selection-toolbar__swatch wb-selection-toolbar__swatch--none"
              title="Снять маркер"
              aria-label="Снять маркер"
              disabled={anyLocked}
              onClick={() => {
                if (applyMarkToTextSelection({ command: "highlight", value: null })) return;
                applyTextRuns((runs) =>
                  runsSetHighlight(runs, 0, runsPlainText(runs).length, null)
                );
              }}
            >
              ⌫
            </button>
          </div>
        </div>
      ) : null}

      {homogeneousType === "frame" ? (
        <div className="wb-selection-toolbar__section">
          <span className="wb-selection-toolbar__label">Оттенок</span>
          <div className="wb-selection-toolbar__swatches">
            {FRAME_TINT_PRESETS.map((c) => {
              const active =
                selectedObjects.length === 1 &&
                frameTintColor(selectedObjects[0]!).toLowerCase() === c.toLowerCase();
              return (
                <button
                  key={c}
                  type="button"
                  className={`wb-selection-toolbar__swatch${active ? " is-active" : ""}`}
                  style={{
                    background: c,
                    boxShadow: active ? "inset 0 0 0 2px #0f172a" : undefined,
                  }}
                  title={c}
                  disabled={anyLocked}
                  onClick={() => applyPatch(buildFillStylePatch(c))}
                />
              );
            })}
          </div>
          {onExportFramePdf && selectedIds.length === 1 ? (
            <button
              type="button"
              className="wb-selection-toolbar__action-btn"
              title="Скачать рамку как PDF"
              disabled={exportBusy}
              onClick={() => void onExportFramePdf(selectedIds[0]!)}
            >
              PDF
            </button>
          ) : null}
        </div>
      ) : null}



      {strokeStyle && supportsStrokeStyle(homogeneousType) ? (
        <StrokeStylePicker
          disabled={anyLocked}
          stroke={strokeStyle.stroke ?? "#1a1a2e"}
          strokeWidth={strokeStyle.strokeWidth ?? (homogeneousType && SHAPE_TYPES.has(homogeneousType) ? 1 : 3)}
          strokeDash={strokeStyle.strokeDash ?? "solid"}
          includeWavy={homogeneousType === "line" || homogeneousType === "arrow"}
          strokePresets={
            homogeneousType && SHAPE_TYPES.has(homogeneousType)
              ? SHAPE_STROKE_PRESETS
              : undefined
          }
          widthPresets={
            homogeneousType && SHAPE_TYPES.has(homogeneousType)
              ? SHAPE_WIDTH_PRESETS
              : undefined
          }
          onChange={(partial) => {
            applyPatch(buildStrokeStylePatch(partial));
            if (homogeneousType && SHAPE_TYPES.has(homogeneousType)) {
              setShapeSettings({
                ...(partial.stroke ? { stroke: partial.stroke } : {}),
                ...(typeof partial.strokeWidth === "number"
                  ? { strokeWidth: partial.strokeWidth }
                  : {}),
                ...(partial.strokeDash && partial.strokeDash !== "wavy"
                  ? { strokeDash: partial.strokeDash }
                  : {}),
              });
              return;
            }
            if (homogeneousType === "line" || homogeneousType === "arrow") {
              setLineSettings({
                ...(partial.stroke ? { stroke: partial.stroke } : {}),
                ...(typeof partial.strokeWidth === "number"
                  ? { strokeWidth: partial.strokeWidth }
                  : {}),
                ...(partial.strokeDash ? { strokeDash: partial.strokeDash } : {}),
              });
            }
          }}
        />
      ) : null}



      {homogeneousType && SHAPE_TYPES.has(homogeneousType) ? (
        <>
          <div className="wb-selection-toolbar__section">
            <span className="wb-selection-toolbar__label">Заливка</span>
            <div className="wb-selection-toolbar__swatches">
              {SHAPE_FILL_PRESETS.map((c) => {
                const currentFill =
                  selectedObjects.length === 1
                    ? readObjectStyle(selectedObjects[0]!).fill ?? "none"
                    : null;
                const active =
                  currentFill !== null &&
                  String(currentFill).toLowerCase() === c.toLowerCase();
                return (
                  <button
                    key={c}
                    type="button"
                    className={`wb-selection-toolbar__swatch${active ? " is-active" : ""}`}
                    style={{
                      background: c === "none" ? "#fff" : c,
                      boxShadow:
                        c === "none" || c === "#ffffff"
                          ? "inset 0 0 0 1px #cbd5e1"
                          : undefined,
                    }}
                    title={c === "none" ? "Без заливки" : c}
                    disabled={anyLocked}
                    aria-pressed={active}
                    onMouseDown={(ev) => ev.stopPropagation()}
                    onClick={() => {
                      applyPatch(buildFillStylePatch(c));
                      setShapeSettings({ fill: c });
                    }}
                  />
                );
              })}
            </div>
          </div>

          {centerShapes.length ? (
            <div className="wb-selection-toolbar__section">
              <span className="wb-selection-toolbar__label">Центр</span>
              <button
                type="button"
                className={`wb-selection-toolbar__action-btn${centerOn ? " is-active" : ""}`}
                title={centerOn ? "Убрать точку в центре" : "Поставить точку в центре"}
                aria-pressed={centerOn}
                disabled={anyLocked}
                onMouseDown={(ev) => ev.stopPropagation()}
                onClick={() => {
                  applyPatch({ showCenter: !centerOn });
                  setShapeSettings({ showCenter: !centerOn });
                }}
              >
                ⊙
              </button>
            </div>
          ) : null}
        </>
      ) : null}



      {homogeneousType === "image" ? (
        <span className="wb-selection-toolbar__hint">Изображение · {selectedIds.length}</span>
      ) : null}



      {homogeneousType === "pdf_document" && selectedIds.length === 1 ? (
        <div className="wb-selection-toolbar__section">
          <span className="wb-selection-toolbar__label">PDF</span>
          <div className="wb-selection-toolbar__pdf-actions">
            <button
              type="button"
              className="wb-selection-toolbar__action-btn"
              title="Предыдущая страница"
              onClick={() => {
                const scene = sceneRef.current;
                const obj = scene?.findObject(selectedIds[0]!);
                if (!scene || !obj || obj.type !== "pdf_document") return;
                const cur = typeof obj.currentPage === "number" ? obj.currentPage : 0;
                if (cur > 0) void scene.setPdfPage(obj.id, cur - 1);
              }}
            >
              ‹
            </button>
            <span className="wb-selection-toolbar__hint">
              {(() => {
                const obj = sceneRef.current?.findObject(selectedIds[0]!);
                if (!obj || obj.type !== "pdf_document") return "—";
                const cur = typeof obj.currentPage === "number" ? obj.currentPage : 0;
                const total = typeof obj.pageCount === "number" ? obj.pageCount : 1;
                return `${cur + 1} / ${total}`;
              })()}
            </span>
            <button
              type="button"
              className="wb-selection-toolbar__action-btn"
              title="Следующая страница"
              onClick={() => {
                const scene = sceneRef.current;
                const obj = scene?.findObject(selectedIds[0]!);
                if (!scene || !obj || obj.type !== "pdf_document") return;
                const cur = typeof obj.currentPage === "number" ? obj.currentPage : 0;
                const total = typeof obj.pageCount === "number" ? obj.pageCount : 1;
                if (cur < total - 1) void scene.setPdfPage(obj.id, cur + 1);
              }}
            >
              ›
            </button>
            {onPresentPdf ? (
              <button
                type="button"
                className="wb-selection-toolbar__action-btn"
                title="Режим презентации (только у вас)"
                onClick={() => onPresentPdf(selectedIds[0]!)}
              >
                ▶
              </button>
            ) : null}
            {onBroadcastPdf ? (
              <button
                type="button"
                className="wb-selection-toolbar__action-btn wb-selection-toolbar__action-btn--broadcast"
                title="Трансляция для всех на доске"
                onClick={() => onBroadcastPdf(selectedIds[0]!)}
              >
                📡
              </button>
            ) : null}
            {onDownloadPresentation ? (
              <button
                type="button"
                className="wb-selection-toolbar__action-btn"
                title="Скачать презентацию (PDF)"
                onClick={() => void onDownloadPresentation(selectedIds[0]!)}
              >
                ↓
              </button>
            ) : null}
          </div>
        </div>
      ) : null}



      {selectedIds.length > 1 ? (
        <span className="wb-selection-toolbar__count">{selectedIds.length} объектов</span>
      ) : null}
    </div>
  );

  return createPortal(toolbar, document.body);
}

